"use client";

import { useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

import { DownloadIcon } from "@/components/appIcons";
import { Button } from "@/components/ui/button";
import { usePwaInstall } from "@/hooks/use-pwa-install";
import { cn } from "@/lib/utils";

export function PwaInstallPromotion({
  surface,
  tone,
}: {
  surface: "alerts_page" | "notification_settings";
  tone: "alerts" | "settings";
}) {
  const t = useTranslations("pwaInstall");
  const { canInstall, dismiss, prompt } = usePwaInstall();
  const [pending, setPending] = useState(false);
  if (!canInstall) return null;

  return (
    <div
      className={cn("mt-5 border-t pt-5", tone === "alerts" ? "border-white/10" : "border-border")}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-lg",
            tone === "alerts" ? "bg-[#e66a3a]/15 text-[#f4a17f]" : "bg-primary/15 text-primary",
          )}
        >
          <DownloadIcon className="text-xl" decorative />
        </span>
        <div className="min-w-0">
          <p className="font-black">{t("title")}</p>
          <p
            className={cn(
              "mt-1 text-sm leading-5",
              tone === "alerts" ? "text-[#aaa79f]" : "text-muted-foreground",
            )}
          >
            {t("description")}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          className={cn(tone === "alerts" && "bg-[#e66a3a] text-[#17130f] hover:bg-[#f17b4d]")}
          disabled={pending}
          onClick={async () => {
            setPending(true);
            const outcome = await prompt(surface);
            setPending(false);
            if (outcome === "error") toast.error(t("error"));
          }}
        >
          <DownloadIcon decorative />
          {pending ? t("installing") : t("cta")}
        </Button>
        <Button
          className={cn(tone === "alerts" && "text-[#cbc7be] hover:bg-white/10 hover:text-white")}
          disabled={pending}
          onClick={() => dismiss(surface)}
          variant="ghost"
        >
          {t("dismiss")}
        </Button>
      </div>
    </div>
  );
}
